import { create } from "zustand";
import { supabase } from 'src/lib/supabaseClient';
import { camelToSnake } from 'src/lib/utils';


// Types for tutor data
export interface TutorAvailabilitySlot {
  day_of_week: number; // 0 = Sunday, 6 = Saturday
  start_time: string;
  end_time: string;
  status: "available" | "auto_accept" | "manual_approval";
}

// Education structure
export interface TutorEducation {
  gpa?: number;
  field: string;
  degree: string;
  country: string;
  institution: string;
  year_of_completion: number;
  is_custom_degree: boolean;
  gpa_total?: number;
  level: string;
}

// Experience structure
export interface TutorExperience {
  role: "online" | "private" | "assessment" | "classroom" | "curriculum" | string;
  years: number;
  end_year?: number | null;
  location: string;
  start_year?: number | null;
  organization: string;
}

// Represents the tutor data from the database
export interface TutorDBRecord {
  id: string;
  education?: TutorEducation[]; // jsonb array in the database
  hourly_rate?: number;
  // Older records may contain a comma-separated string, while the current
  // database column is text[]. Support both while the remaining legacy data
  // is migrated.
  subjects_taught?: string | string[];
  experience?: TutorExperience[]; // jsonb array in the database
  bio?: string;
  verification_status?: string;
  cv_file_path?: string;
  rating?: number;
  date_of_birth?: string;
  created_at: string;
  updated_at: string;
}

// Represents the combined tutor data with profile information
export interface Tutor extends TutorDBRecord {
  // Fields from profiles table
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url?: string; // This is the field name in the database schema
  timezone?: string;

  // Computed or transformed fields
  availability_slots?: TutorAvailabilitySlot[];
  subjects?: string[]; // Parsed from subjects_taught
}

// Tutor store state and actions
interface TutorStore {
  // State
  tutors: Tutor[];
  selectedTutor: Tutor | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchTutors: () => Promise<void>;
  fetchTutorById: (id: string) => Promise<Tutor | null>;
  createTutor: (tutor: {
    id: string; // User ID is required
    education?: TutorEducation[];
    hourly_rate?: number;
    subjects_taught?: string | string[];
    experience?: TutorExperience[];
    bio?: string;
    verification_status?: string;
    cv_file_path?: string;
    date_of_birth?: string;
    availability_slots?: TutorAvailabilitySlot[];
  }) => Promise<Tutor | null>;
  updateTutor: (id: string, updates: Partial<Tutor>) => Promise<boolean>;
  deleteTutor: (id: string) => Promise<boolean>;
  setSelectedTutor: (tutor: Tutor | null) => void;
  searchTutors: (query: string) => Tutor[];
  filterTutorsBySubject: (subjectId: string) => Tutor[];
}

type TutorTableUpdates = Pick<
  TutorDBRecord,
  | "education"
  | "hourly_rate"
  | "subjects_taught"
  | "experience"
  | "bio"
  | "verification_status"
  | "cv_file_path"
  | "date_of_birth"
>;

type ProfileTableUpdates = Pick<
  Tutor,
  "first_name" | "last_name" | "email" | "profile_picture_url" | "timezone"
>;

const toSubjects = (value?: string | string[] | null): string[] => {
  if (Array.isArray(value)) {
    return value.map((subject) => subject.trim()).filter(Boolean);
  }

  return value ? value.split(",").map((subject) => subject.trim()).filter(Boolean) : [];
};

const fetchAvailabilitySlotsByTutorIds = async (tutorIds: string[]) => {
  if (tutorIds.length === 0) return new Map<string, TutorAvailabilitySlot[]>();

  const { data, error } = await supabase
    .from("tutor_availability")
    .select("tutor_id, day_of_week, start_time, end_time, status")
    .in("tutor_id", tutorIds)
    .order("day_of_week", { ascending: true })
    .order("start_time", { ascending: true });

  if (error) throw error;

  const slotsByTutorId = new Map<string, TutorAvailabilitySlot[]>();

  data?.forEach((slot) => {
    const tutorSlots = slotsByTutorId.get(slot.tutor_id) || [];
    tutorSlots.push({
      day_of_week: slot.day_of_week,
      start_time: slot.start_time,
      end_time: slot.end_time,
      status: slot.status,
    });
    slotsByTutorId.set(slot.tutor_id, tutorSlots);
  });

  return slotsByTutorId;
};

const replaceTutorAvailabilitySlots = async (
  tutorId: string,
  availabilitySlots: TutorAvailabilitySlot[] = []
) => {
  const { error: deleteError } = await supabase
    .from("tutor_availability")
    .delete()
    .eq("tutor_id", tutorId);

  if (deleteError) throw deleteError;

  if (availabilitySlots.length === 0) return;

  const { error: insertError } = await supabase
    .from("tutor_availability")
    .insert(
      availabilitySlots.map((slot) => ({
        tutor_id: tutorId,
        day_of_week: slot.day_of_week,
        start_time: slot.start_time,
        end_time: slot.end_time,
        status: slot.status,
      }))
    );

  if (insertError) throw insertError;
};

// Create the store
export const useTutorStore = create<TutorStore>((set, get) => ({
  // Initial state
  tutors: [],
  selectedTutor: null,
  isLoading: false,
  error: null,

  // Actions
  fetchTutors: async () => {
    set({ isLoading: true, error: null });

    try {

      // Join tutors table with profiles to get complete tutor information
      const { data, error } = await supabase
        .from("tutors")
        .select(`
          id,
          education,
          hourly_rate,
          subjects_taught,
          experience,
          bio,
          verification_status,
          cv_file_path,
          rating,
          date_of_birth,
          created_at,
          updated_at,
          profiles:id (
            first_name,
            last_name,
            email,
            profile_picture_url,
            timezone
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const slotsByTutorId = await fetchAvailabilitySlotsByTutorIds(data?.map((tutor) => tutor.id) || []);

      // Transform the data to match our Tutor interface
      const transformedData = data?.map(tutor => {
        const profile = tutor.profiles || {};

        // Type assertion for profile to avoid TypeScript errors
        const typedProfile = profile as {
          first_name?: string;
          last_name?: string;
          email?: string;
          profile_picture_url?: string;
          timezone?: string;
        };

        const subjects = toSubjects(tutor.subjects_taught);

        // Create a new object without the profiles property
        const { profiles: _, ...restData } = tutor;

        return {
          ...restData,
          first_name: typedProfile.first_name || '',
          last_name: typedProfile.last_name || '',
          email: typedProfile.email || '',
          profile_picture_url: typedProfile.profile_picture_url,
          timezone: typedProfile.timezone,
          subjects,
          availability_slots: slotsByTutorId.get(tutor.id) || []
        };
      });

      set({ tutors: transformedData || [], isLoading: false });
    } catch (error) {
      console.error("Error fetching tutors:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to load tutors",
        isLoading: false
      });
    }
  },

  fetchTutorById: async (id) => {
    set({ isLoading: true, error: null });

    try {

      const { data, error } = await supabase
        .from("tutors")
        .select(`
          id,
          education,
          hourly_rate,
          subjects_taught,
          experience,
          bio,
          verification_status,
          cv_file_path,
          rating,
          date_of_birth,
          created_at,
          updated_at,
          profiles:id (
            first_name,
            last_name,
            email,
            profile_picture_url,
            timezone
          )
        `)
        .eq("id", id)
        .single();

      if (error) throw error;

      // Transform the data to match our Tutor interface
      const profile = data.profiles || {};

      const subjects = toSubjects(data.subjects_taught);
      const slotsByTutorId = await fetchAvailabilitySlotsByTutorIds([id]);

      // Create a new object without the profiles property
      const { profiles: _, ...restData } = data;

      // Type assertion for profile to avoid TypeScript errors
      const typedProfile = profile as {
        first_name?: string;
        last_name?: string;
        email?: string;
        profile_picture_url?: string;
        timezone?: string;
      };

      const transformedData: Tutor = {
        ...restData,
        first_name: typedProfile.first_name || '',
        last_name: typedProfile.last_name || '',
        email: typedProfile.email || '',
        profile_picture_url: typedProfile.profile_picture_url,
        timezone: typedProfile.timezone,
        subjects,
        availability_slots: slotsByTutorId.get(id) || []
      };

      set({ selectedTutor: transformedData, isLoading: false });
      return transformedData;
    } catch (error) {
      console.error(`Error fetching tutor with ID ${id}:`, error);
      set({
        error: error instanceof Error ? error.message : `Failed to load tutor with ID ${id}`,
        isLoading: false
      });
      return null;
    }
  },

  createTutor: async (tutorData) => {
    set({ isLoading: true, error: null });

    try {

      // Extract fields that belong to the tutors table
      const {
        id, // User ID must be provided
        education,
        hourly_rate,
        subjects_taught,
        experience,
        bio,
        verification_status,
        cv_file_path,
        date_of_birth,
        availability_slots
      } = tutorData;

      if (!id) {
        throw new Error("User ID is required to create a tutor profile");
      }

      // Insert into tutors table
      const { data, error } = await supabase
        .from("tutors")
        .insert(camelToSnake({
          id,
          education,
          hourly_rate,
          subjects_taught,
          experience,
          bio,
          verification_status,
          cv_file_path,
          date_of_birth
        }))
        .select()
        .single();

      if (error) throw error;

      if (availability_slots) {
        await replaceTutorAvailabilitySlots(data.id, availability_slots);
      }

      // Fetch the profile data to combine with tutor data
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("first_name, last_name, email, profile_picture_url, timezone")
        .eq("id", data.id)
        .single();

      if (profileError) throw profileError;

      // Combine the data
      const combinedData: Tutor = {
        ...data,
        first_name: profileData.first_name || '',
        last_name: profileData.last_name || '',
        email: profileData.email || '',
        profile_picture_url: profileData.profile_picture_url,
        timezone: profileData.timezone,
        subjects: toSubjects(data.subjects_taught),
        availability_slots: availability_slots || []
      };

      // Update the tutors list with the new tutor
      set(state => ({
        tutors: [...state.tutors, combinedData],
        isLoading: false
      }));

      return combinedData;
    } catch (error) {
      console.error("Error creating tutor:", error);
      set({
        error: error instanceof Error ? error.message : "Failed to create tutor",
        isLoading: false
      });
      return null;
    }
  },

  updateTutor: async (id, updates) => {
    set({ isLoading: true, error: null });

    try {

      // Extract fields that belong to the tutors table
      const {
        education,
        hourly_rate,
        subjects_taught,
        experience,
        bio,
        verification_status,
        cv_file_path,
        date_of_birth,
        availability_slots,
        first_name,
        last_name,
        email,
        profile_picture_url,
        timezone
      } = updates;

      // Prepare tutor table updates
      const tutorUpdates: Partial<TutorTableUpdates> = {};

      if (education !== undefined) tutorUpdates.education = education;
      if (hourly_rate !== undefined) tutorUpdates.hourly_rate = hourly_rate;
      if (subjects_taught !== undefined) tutorUpdates.subjects_taught = subjects_taught;
      if (experience !== undefined) tutorUpdates.experience = experience;
      if (bio !== undefined) tutorUpdates.bio = bio;
      if (verification_status !== undefined) tutorUpdates.verification_status = verification_status;
      if (cv_file_path !== undefined) tutorUpdates.cv_file_path = cv_file_path;
      if (date_of_birth !== undefined) tutorUpdates.date_of_birth = date_of_birth;

      // Update tutors table if there are tutor-specific updates
      if (Object.keys(tutorUpdates).length > 0) {
        const { error } = await supabase
          .from("tutors")
          .update(camelToSnake(tutorUpdates))
          .eq("id", id);

        if (error) throw error;
      }

      if (availability_slots !== undefined) {
        await replaceTutorAvailabilitySlots(id, availability_slots);
      }

      // Prepare profile table updates
      const profileUpdates: Partial<ProfileTableUpdates> = {};

      if (first_name !== undefined) profileUpdates.first_name = first_name;
      if (last_name !== undefined) profileUpdates.last_name = last_name;
      if (email !== undefined) profileUpdates.email = email;
      if (profile_picture_url !== undefined) profileUpdates.profile_picture_url = profile_picture_url;
      if (timezone !== undefined) profileUpdates.timezone = timezone;

      // Update profiles table if there are profile-specific updates
      if (Object.keys(profileUpdates).length > 0) {
        const { error } = await supabase
          .from("profiles")
          .update(camelToSnake(profileUpdates))
          .eq("id", id);

        if (error) throw error;
      }

      // Fetch the updated tutor to ensure we have the latest data
      const updatedTutor = await get().fetchTutorById(id);

      if (!updatedTutor) {
        throw new Error(`Failed to fetch updated tutor with ID ${id}`);
      }

      // Update the tutors list with the updated tutor
      set(state => ({
        tutors: state.tutors.map(tutor =>
          tutor.id === id ? updatedTutor : tutor
        ),
        selectedTutor: updatedTutor,
        isLoading: false
      }));

      return true;
    } catch (error) {
      console.error(`Error updating tutor with ID ${id}:`, error);
      set({
        error: error instanceof Error ? error.message : `Failed to update tutor with ID ${id}`,
        isLoading: false
      });
      return false;
    }
  },

  deleteTutor: async (id) => {
    set({ isLoading: true, error: null });

    try {

      const { error } = await supabase
        .from("tutors")
        .delete()
        .eq("id", id);

      if (error) throw error;

      // Remove the deleted tutor from the tutors list
      set(state => ({
        tutors: state.tutors.filter(tutor => tutor.id !== id),
        selectedTutor: state.selectedTutor?.id === id ? null : state.selectedTutor,
        isLoading: false
      }));

      return true;
    } catch (error) {
      console.error(`Error deleting tutor with ID ${id}:`, error);
      set({
        error: error instanceof Error ? error.message : `Failed to delete tutor with ID ${id}`,
        isLoading: false
      });
      return false;
    }
  },

  setSelectedTutor: (tutor) => {
    set({ selectedTutor: tutor });
  },

  searchTutors: (query) => {
    const { tutors } = get();
    const lowercaseQuery = query.toLowerCase();

    return tutors.filter(tutor =>
      tutor.first_name.toLowerCase().includes(lowercaseQuery) ||
      tutor.last_name.toLowerCase().includes(lowercaseQuery) ||
      tutor.email.toLowerCase().includes(lowercaseQuery) ||
      (tutor.subjects && tutor.subjects.some((subject: string) =>
        subject.toLowerCase().includes(lowercaseQuery)
      )) ||
      (tutor.experience && Array.isArray(tutor.experience) &&
        tutor.experience.some((exp: TutorExperience) =>
          exp.role.toLowerCase().includes(lowercaseQuery) ||
          exp.organization.toLowerCase().includes(lowercaseQuery)
        )
      )
    );
  },

  filterTutorsBySubject: (subjectId) => {
    const { tutors } = get();

    return tutors.filter(tutor =>
      tutor.subjects && tutor.subjects.includes(subjectId)
    );
  }
}));
